import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import moJenniferImg from '../mo_jennifer_img.jpg';
import moMaxGaugeImg from '../mo_maxGauge_img.jpg';
import moTamisImg from '../mo_tamis_img.jpg';
import moEgEnterpriseImg from '../mo_eGEnterprise_img.jpg';
import moOwlensImg from '../OWLens.jpg';
import { addMobileDetectionListener } from '../utils/deviceDetection';
import '../App.css';

const MonitoringPage = () => {
  const location = useLocation();
  const [activeCategory, setActiveCategory] = useState('jennifer');
  const [isMobile, setIsMobile] = useState(false);
  
  // 각 섹션에 대한 ref 생성
  const jenniferRef = useRef(null);
  const maxgaugeRef = useRef(null);
  const tamisRef = useRef(null);
  const egEnterpriseRef = useRef(null);
  const owlensRef = useRef(null);

  const categories = [
    { id: 'jennifer', name: 'Jennifer', title: 'Jennifer APM' },
    { id: 'maxgauge', name: 'MaxGauge', title: 'MaxGauge Monitoring' },
    { id: 'tamis', name: 'TAMIS', title: 'TAMIS Solution' },
    { id: 'eg-enterprise', name: 'eG Enterprise', title: 'eG Enterprise Monitoring' },
    { id: 'owlens', name: 'OWLens', title: 'OWLens' }
  ];

  const refs = {
    jennifer: jenniferRef,
    maxgauge: maxgaugeRef,
    tamis: tamisRef,
    'eg-enterprise': egEnterpriseRef,
    owlens: owlensRef
  };

  // 모바일 감지 설정
  useEffect(() => {
    const cleanupMobileListener = addMobileDetectionListener(setIsMobile);
    return () => {
      cleanupMobileListener();
    };
  }, []);

  // URL 파라미터 확인하여 초기 카테고리 설정
  useEffect(() => {
    // 페이지 로드 시 스크롤을 맨 위로 이동
    window.scrollTo(0, 0);
    
    const params = new URLSearchParams(location.search);
    const category = params.get('category');
    if (category && categories.find(cat => cat.id === category)) {
      setActiveCategory(category);
      // 페이지 로드 후 해당 섹션으로 스크롤
      setTimeout(() => {
        const targetRef = refs[category];
        if (targetRef && targetRef.current) {
          const headerOffset = isMobile ? 70 : 88;
          const elementPosition = targetRef.current.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [location.search, isMobile]);

  // Intersection Observer 설정
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -70% 0px', // 상단 20%, 하단 70% 위치에서 트리거
      threshold: 0
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // 현재 보이는 섹션의 카테고리 ID 찾기
          const categoryId = Object.keys(refs).find(key => 
            refs[key].current === entry.target
          );
          if (categoryId) {
            setActiveCategory(categoryId);
          }
        }
      });
    }, observerOptions);

    // 모든 섹션을 관찰 대상으로 등록
    Object.values(refs).forEach(ref => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  const handleCategoryClick = (categoryId) => {
    setActiveCategory(categoryId);
    const targetRef = refs[categoryId];
    if (targetRef && targetRef.current) {
      // 상단 고정 헤더 높이 (68px) + 여유 공간 (20px) = 88px
      const headerOffset = isMobile ? 70 : 88;
      const elementPosition = targetRef.current.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className={`subcustom-header-container ${isMobile ? 'mobile-device' : ''}`}>
      <header className="subcustom-header">
        <div className="hero-title-area">
          <div className="hero-subtitle">MONITORING</div>
          <h2 className="hero-title">모니터링 & 매니지먼트</h2>
        </div>
      </header>

      <main className="infra-main">
        {/* 모바일에서는 카테고리 탭 숨김 */}

        <div className="solutions-layout">
          {/* 왼쪽 사이드바 카테고리 메뉴 - 데스크톱에서만 표시 */}
          {!isMobile && (
            <div className="sidebar-categories">
              <h3 className="sidebar-title">모니터링</h3>
              <ul className="category-menu">
                {categories.map(category => (
                  <li key={category.id}>
                    <button
                      className={`category-item ${activeCategory === category.id ? 'active' : ''}`}
                      onClick={() => handleCategoryClick(category.id)}
                    >
                      {category.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* 오른쪽 콘텐츠 영역 */}
          <div className="content-area">
            {/* Jennifer APM 섹션 */}
            <div className="solution-section" ref={jenniferRef}>
              <h2 className="section-title">Jennifer X 5.0(Kubernetes) 
              </h2>
              <div className="product-content">
                <div className="product-text">
                  <p>제니퍼소프트 제조사 공식 파트너</p>
                  <div className="feature-list">
                    <h3>주요 특징:</h3>
                    <ul>
                      <li>국내 APM 시장 점유율 1위</li>
                      <li>1650+ 이상의 국내외 레퍼런스</li>
                      <li>쿠버네티스 클러스터 시각화/실시간 파드상태/노드 리소스 사용량/컨테이너 상태확인</li>
                      <li>GS인증제품</li>
                      <li>나라장터 디지털서비스몰 등록</li>
                    </ul>
                  </div>
                </div>
                <div className="product-image">
                  <img src={moJenniferImg} alt="Jennifer APM" />
                </div>
              </div>
            </div>

            {/* MaxGauge Monitoring 섹션 */}
            <div className="solution-section" ref={maxgaugeRef}>
              <h2 className="section-title">MaxGauge 5.0
              </h2>
              <div className="product-content">
                <div className="product-text">
                  <p>엑셈 제조사 공식 파트너</p>
                  <div className="feature-list">
                    <h3>주요 특징:</h3>
                    <ul>
                      <li>국내 DBMS 성능관리솔루션 시장 점유율 1위</li>
                      <li>exemONE 출시 (다양한 영역의 모니터링을 하나의 플랫폼에서 제공하는 All-in One<br/> 통합모니터링 솔루션)</li>
                      <li>GS인증제품</li>
                      <li>나라장터 디지털서비스몰 등록</li>
                    </ul>
                  </div>
                </div>
                <div className="product-image">
                  <img src={moMaxGaugeImg} alt="MaxGauge Monitoring" />
                </div>
              </div>
            </div>

            {/* TAMIS Solution 섹션 */}
            <div className="solution-section" ref={tamisRef}>
              <h2 className="section-title">TAMIS 2.0
                <br />(IT 서비스 운영 효율화 솔루션)
              </h2>
              <div className="product-content">
                <div className="product-text">
                  <p>두썸소프트 제조사 타미스 총판사</p>
                  <div className="feature-list">
                    <h3>주요 특징:</h3>
                    <ul>
                      <li>서버와 프로세스 연결관계(인터페이스)관점의 국내 유일의 인터페이스 플로우맵 기반<br/> 솔루션</li>
                      <li>통합 로그모니터링 환경 제공 (운영환경 주요로그파일 감시/실시간 모니터링 및 외부알림/브라우저 기반 빠른 검색,로그리더)</li>
                      <li>선제 지능성 경보 시스템</li>
                      <li>DPM Lite 기능</li>
                      <li>GS인증제품</li>
                      <li>나라장터 디지털서비스몰 등록</li>
                    </ul>
                  </div>
                </div>
                <div className="product-image">
                  <img src={moTamisImg} alt="TAMIS Solution" />
                </div>
              </div>
            </div>

            {/* eG Enterprise Monitoring 섹션 */}
            <div className="solution-section" ref={egEnterpriseRef}>
              <h2 className="section-title">eG Enterprise 7.2
                <br />(애플리케이션 및 IT인프라 통합 모니터링)
              </h2>
              <div className="product-content">
                <div className="product-text">
                  <p>이지이노베이션코리아 공식 파트너</p>
                  <div className="feature-list">
                    <h3>주요 특징:</h3>
                    <ul>
                      <li>애플리케이션과 IT인프라의 통합된 성능관리 솔루션</li>
                      <li>IT인프라 모니터링에 대한 옵저버빌리티 제시</li>
                      <li>AlOps(자체 머신러닝 기능)를 통한 RCA(Root Cause Analysis)구현</li>
                      <li>VDI환경(Vmware, Citrix, Nutanix등 다양한 환경), SAP에 대한 End-to-End<br/> 성능모니터링</li>
                      <li>타 솔루션에 없는 수직/수평적 상관관계 내장 (특허보유)</li>
                      <li>GS인증/나라장터 디지털서비스몰 등록</li>
                    </ul>
                  </div>
                </div>
                <div className="product-image">
                  <img src={moEgEnterpriseImg} alt="eG Enterprise Monitoring" />
                </div>
              </div>
            </div>

            {/* OWLens 섹션 */}
            <div className="solution-section" ref={owlensRef}>
              <h2 className="section-title">OWLens
                <br/>(SAP 성능관리 솔루션)
              </h2>
              <div className="product-content">
                <div className="product-text">
                  <p>메타코어글로벌 제조사 파트너</p>
                  <div className="feature-list">
                    <h3>주요 특징:</h3>
                    <ul>
                      <li>통합 대시보드 제공으로 직관적인 모니터링 가능</li>
                      <li>성능관리 모니터링 표준 RFC/JMX 방식 이용</li>
                      <li>트랜잭션 상세 모니터링. 초 단위 SSR 상세 데이터 수집 및 모니터링</li>
                      <li>ABAP Dump/ System Log/ Application Log 실시간 모니터링 및 상세 분석 기능 제공</li>
                      <li>KPI 기반 실시간 Alert 제공 및 Context Analyzer로 장애원인 즉시 규명 가능</li>
                      <li>장기 추세 데이터 축척+Time Navigator, Expensive SQL, Top N List, Role Tracker 등 기능 제공</li>
                    </ul>
                  </div>
                </div>
                <div className="product-image">
                  <img src={moOwlensImg} alt="OWLens" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MonitoringPage; 